/**
 * The wallet as a fanned hand of cards — pick one by looking at it.
 *
 * Replaced the dashboard's 3D stack, which the user found pretty but hard to
 * read: at that tilt every card was a coloured sliver, and nothing said which
 * sliver was which. A hand of cards keeps the physical feel and fixes that:
 *
 *   * **Every card carries a spine** — issuer and last four, written up its
 *     left edge like a book on a shelf. That edge is the part of each card a
 *     fan leaves visible, so every card is identifiable without hovering.
 *   * **A chip row under the fan names every card in full.** Hovering a chip
 *     lifts its card; hovering a card lights its chip. Two ways in, one state.
 *   * **The selected card is drawn out of the hand** — straightened and lifted
 *     clear above it — and the rest dim, so "which one am I looking at" is
 *     never a question, and the drawn card hides no other card's spine. Nothing moves on its own — the old
 *     stack rotated every four seconds, under the user's pointer.
 *
 * Geometry is measured, not guessed: the stage's width sets the card size and
 * how wide the fan may open, so nine cards fit a phone and one card doesn't
 * float in a desktop panel. Reduced motion keeps the layout and drops the
 * travel.
 */

import { useReducedMotion } from "framer-motion";
import { motion } from "framer-motion";
import * as React from "react";

import { CardSwatch } from "@/components/ui/primitives";
import type { CardTheme } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatLast4 } from "@/lib/format";

const FALLBACK_THEME: CardTheme = {
  gradient_from: "#1c2431",
  gradient_to: "#2b3546",
  foreground: "#f5f3ee",
};

export interface FanItem {
  id: string;
  issuer: string;
  name: string;
  last4: string;
  theme: CardTheme | null;
  /** One figure on the card's face, e.g. its outstanding or its cashback. */
  figure?: { label: string; value: string } | null;
}

interface CardFanProps {
  items: FanItem[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Adds an "All cards" chip that clears the selection. */
  allowAll?: boolean;
  allLabel?: string;
  /** Upper bound on a card's width, px. The stage may make it smaller. */
  maxCardWidth?: number;
  className?: string;
  /** Accessible name for the chip row. */
  label?: string;
}

/** The pivot sits this many card-heights below a card's top edge. */
const PIVOT = 3.1;
const ASPECT = 1.586;

export function CardFan({
  items,
  selectedId,
  onSelect,
  allowAll = false,
  allLabel = "All cards",
  maxCardWidth = 236,
  className,
  label = "Choose a card",
}: CardFanProps) {
  const reduce = useReducedMotion();
  const stageRef = React.useRef<HTMLDivElement>(null);
  const [stageWidth, setStageWidth] = React.useState(0);
  const [hoverId, setHoverId] = React.useState<string | null>(null);
  const [entered, setEntered] = React.useState(false);

  React.useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => entry && setStageWidth(entry.contentRect.width));
    ro.observe(el);
    setStageWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  React.useEffect(() => {
    const t = setTimeout(() => setEntered(true), 90);
    return () => clearTimeout(t);
  }, []);

  const n = items.length;
  const geo = React.useMemo(() => layout(n, stageWidth, maxCardWidth), [n, stageWidth, maxCardWidth]);
  const selectedIndex = items.findIndex((i) => i.id === selectedId);
  // Room above the hand for the drawn card. Only reserved while one is drawn,
  // so an "all cards" view doesn't leave an empty band over the fan.
  const drawn = selectedIndex !== -1 && n > 1;
  const headroom = drawn ? geo.cardHeight * 1.02 : 0;

  return (
    <div className={cn("flex flex-col", className)}>
      {/* The stage. Cards are positioned from its centre-top; nothing here is
          in normal flow, so its height is set from the measured geometry. */}
      <div
        ref={stageRef}
        className="relative w-full select-none"
        style={{
          height: (geo.stageHeight || 220) + headroom,
          transition: reduce ? "none" : "height 0.6s cubic-bezier(0.16,1,0.3,1)",
        }}
        onPointerLeave={() => setHoverId(null)}
      >
        {stageWidth > 0 &&
          items.map((item, i) => {
            const angle = geo.angles[i] ?? 0;
            const selected = item.id === selectedId;
            const hovered = item.id === hoverId;
            const dimmed = selectedId !== null && !selected && !hovered;
            // The selected card is drawn out of the hand: straightened and lifted
            // clear of it, so it covers no other card's spine. Its slot stays
            // empty, which shows where it came from.
            const transform = !entered
              ? `translateX(-50%) rotate(0deg) translateY(${geo.cardHeight * 0.35}px) scale(0.94)`
              : selected && drawn
                ? `translateX(-50%) rotate(0deg) translateY(${-headroom}px) scale(1.04)`
                : `translateX(-50%) rotate(${angle}deg) translateY(${hovered ? -geo.cardHeight * 0.1 : 0}px)`;
            return (
              <button
                key={item.id}
                type="button"
                tabIndex={-1}
                aria-label={`Select ${item.name} ending ${item.last4}`}
                aria-pressed={selected}
                onClick={() => onSelect(selected && allowAll ? null : item.id)}
                onPointerEnter={() => setHoverId(item.id)}
                className="absolute left-1/2 block rounded-card text-left focus:outline-none"
                style={{
                  top: geo.topPad + headroom,
                  width: geo.cardWidth,
                  height: geo.cardHeight,
                  transformOrigin: `50% ${PIVOT * 100}%`,
                  transform,
                  // Selected on top; otherwise the hand's natural order.
                  zIndex: selected ? 200 : 10 + i,
                  opacity: entered ? 1 : 0,
                  filter: dimmed ? "brightness(0.62) saturate(0.75)" : "none",
                  transition: reduce
                    ? "none"
                    : `transform 0.75s cubic-bezier(0.16,1,0.3,1) ${entered ? 0 : 0.05 + i * 0.05}s, top 0.6s cubic-bezier(0.16,1,0.3,1), opacity 0.5s ease ${entered ? 0 : i * 0.05}s, filter 0.45s ease`,
                }}
              >
                <FanFace item={item} selected={selected} compact={geo.cardWidth < 175} />
              </button>
            );
          })}
      </div>

      <CardChips
        className="mt-4 sm:justify-center"
        items={items}
        selectedId={selectedId}
        onSelect={onSelect}
        allowAll={allowAll}
        allLabel={allLabel}
        hoverId={hoverId}
        onHover={setHoverId}
        label={label}
      />
    </div>
  );
}

/**
 * Every card by name, as a row of chips. The highlight pill slides between
 * chips (a shared layoutId), and arrow keys move the selection. Shared by the
 * fan and by pages that pick a card without one.
 */
export function CardChips({
  items,
  selectedId,
  onSelect,
  allowAll = false,
  allLabel = "All cards",
  hoverId = null,
  onHover,
  label = "Choose a card",
  className,
}: {
  items: Pick<FanItem, "id" | "issuer" | "name" | "last4" | "theme">[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  allowAll?: boolean;
  allLabel?: string;
  hoverId?: string | null;
  onHover?: (id: string | null) => void;
  label?: string;
  className?: string;
}) {
  const pillId = React.useId();
  const n = items.length;
  const selectedIndex = items.findIndex((i) => i.id === selectedId);
  const hover = onHover ?? (() => {});

  const move = (delta: number) => {
    if (n === 0) return;
    const from = selectedIndex === -1 ? (delta > 0 ? -1 : n) : selectedIndex;
    const next = from + delta;
    if (next < 0 || next >= n) {
      if (allowAll) onSelect(null);
      else onSelect(items[(next + n) % n]?.id ?? null);
      return;
    }
    onSelect(items[next]?.id ?? null);
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        // One swipeable row on a phone; wrapped and centred from sm up.
        "-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0",
        className,
      )}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") {
          e.preventDefault();
          move(1);
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          move(-1);
        }
      }}
    >
      {allowAll && (
        <Chip
          active={selectedId === null}
          pillId={pillId}
          onClick={() => onSelect(null)}
          onHover={() => hover(null)}
        >
          <span className="px-0.5">{allLabel}</span>
        </Chip>
      )}
      {items.map((item) => (
        <Chip
          key={item.id}
          active={item.id === selectedId}
          lit={item.id === hoverId}
          pillId={pillId}
          onClick={() => onSelect(item.id === selectedId && allowAll ? null : item.id)}
          onHover={(on) => hover(on ? item.id : null)}
          title={`${item.issuer} · ${item.name} · ${item.last4}`}
        >
          <CardSwatch theme={item.theme} />
          <span className="max-w-[9.5rem] truncate">{item.name}</span>
          <span className="font-mono text-[10.5px] text-faint">{item.last4}</span>
        </Chip>
      ))}
    </div>
  );
}

function Chip({
  active,
  lit = false,
  pillId,
  onClick,
  onHover,
  title,
  children,
}: {
  active: boolean;
  lit?: boolean;
  pillId: string;
  onClick: () => void;
  onHover: (on: boolean) => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      title={title}
      onClick={onClick}
      onPointerEnter={() => onHover(true)}
      onPointerLeave={() => onHover(false)}
      onFocus={() => onHover(true)}
      onBlur={() => onHover(false)}
      className={cn(
        "relative inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] transition-colors duration-300",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50",
        active
          ? "border-brass/50 text-ink"
          : lit
            ? "border-hairline-strong text-ink"
            : "border-hairline text-muted hover:text-ink",
      )}
    >
      {active && (
        <motion.span
          layoutId={pillId}
          aria-hidden
          className="absolute inset-0 -z-0 rounded-full bg-brass/[0.12]"
          transition={{ type: "spring", stiffness: 420, damping: 34 }}
        />
      )}
      <span className="relative z-[1] inline-flex items-center gap-1.5">{children}</span>
    </button>
  );
}

/** A card face built for the fan: a readable spine on the edge that shows. */
function FanFace({
  item,
  selected,
  compact,
}: {
  item: FanItem;
  selected: boolean;
  /** A phone-sized card: the name and spine only, no figure. */
  compact: boolean;
}) {
  const theme = item.theme ?? FALLBACK_THEME;
  return (
    <div
      className={cn(
        "card-object h-full transition-shadow duration-500",
        selected && "shadow-[0_24px_48px_-16px_rgb(0_0_0/0.65),0_0_0_1px_rgb(var(--brass)/0.55)]",
      )}
      style={{
        backgroundImage: [
          "radial-gradient(130% 90% at 0% 0%, rgb(255 255 255 / 0.16), transparent 55%)",
          "radial-gradient(120% 100% at 100% 100%, rgb(0 0 0 / 0.38), transparent 60%)",
          `linear-gradient(140deg, ${theme.gradient_from} 0%, ${theme.gradient_to} 100%)`,
        ].join(", "),
        color: theme.foreground,
      }}
    >
      {/* The spine: issuer and last four up the left edge. */}
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 z-[3] flex w-[1.65rem] items-center justify-center border-r"
        style={{ borderColor: `${theme.foreground}22`, background: "rgb(0 0 0 / 0.14)" }}
      >
        <span
          className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.16em]"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          <span className="opacity-70">{shortIssuer(item.issuer)}</span>
          <span className="mx-1 opacity-40">·</span>
          <span className="font-semibold">{item.last4}</span>
        </span>
      </div>

      <div className="relative z-[3] flex h-full flex-col justify-between py-[7%] pl-[2.35rem] pr-[7%]">
        <div className="min-w-0">
          <div className="truncate font-mono text-[9.5px] uppercase tracking-[0.14em] opacity-70">
            {item.issuer}
          </div>
          <div
            className={cn(
              "embossed mt-0.5 line-clamp-2 font-display font-semibold leading-tight tracking-[-0.02em]",
              compact ? "text-[12.5px]" : "text-[15px]",
            )}
          >
            {item.name}
          </div>
        </div>
        <div className="flex items-end justify-between gap-2">
          {/* With a figure to show, the spine carries the last four alone. */}
          {(!item.figure || compact) && (
            <div className="embossed min-w-0 truncate font-mono text-[12.5px] tracking-[0.18em] opacity-95">
              {compact ? `•• ${item.last4}` : formatLast4(item.last4)}
            </div>
          )}
          {item.figure && !compact && (
            <div className="ml-auto shrink-0 text-right">
              <div className="font-mono text-[8.5px] uppercase tracking-[0.14em] opacity-60">
                {item.figure.label}
              </div>
              <div className="embossed whitespace-nowrap font-display text-[14px] font-semibold tracking-[-0.02em]">
                {item.figure.value}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** "Example Bank" → "Example". The spine has room for one word. */
function shortIssuer(issuer: string): string {
  const first = issuer.trim().split(/\s+/)[0] ?? issuer;
  return first.length > 9 ? first.slice(0, 9) : first;
}

interface Geometry {
  cardWidth: number;
  cardHeight: number;
  angles: number[];
  topPad: number;
  stageHeight: number;
}

/**
 * Card size and fan angles for a stage `width` wide.
 *
 * The card takes ~40% of the stage (capped), then the fan opens as wide as the
 * stage allows — up to 8° a card and 64° in all. Height covers the centre card
 * lifted when selected plus the drop of the outermost cards.
 */
function layout(n: number, width: number, maxCardWidth: number): Geometry {
  if (width <= 0 || n === 0) {
    return { cardWidth: 0, cardHeight: 0, angles: [], topPad: 0, stageHeight: 0 };
  }
  const cardWidth = Math.min(maxCardWidth, Math.max(132, width * (n === 1 ? 0.6 : 0.42)));
  const cardHeight = cardWidth / ASPECT;
  const radius = PIVOT * cardHeight;

  const wanted = Math.min(64, (n - 1) * 8);
  const room = Math.max(0, (width - cardWidth - 8) / 2);
  const maxHalf = (Math.asin(Math.min(1, room / radius)) * 180) / Math.PI;
  const spread = n > 1 ? Math.min(wanted, maxHalf * 2) : 0;
  const step = n > 1 ? spread / (n - 1) : 0;
  const angles = Array.from({ length: n }, (_, i) => -spread / 2 + i * step);

  const half = ((spread / 2) * Math.PI) / 180;
  const topPad = cardHeight * 0.14;
  const drop = radius * (1 - Math.cos(half)) + (cardWidth / 2) * Math.sin(half);
  const stageHeight = topPad + cardHeight + drop + 6;
  return { cardWidth, cardHeight, angles, topPad, stageHeight };
}
